import { useEffect, useRef, useState } from "react";
import { MessageCircleMore } from "lucide-react";
import { useLocation } from "react-router-dom";
import Chatbot from "@/pages/Chatbot";
import "@/components/chat/FloatingChatLauncher.css";

const PRIVATE_ROUTES = ["/signin", "/auth", "/profile", "/check", "/report-ready", "/screen", "/unlock", "/account", "/admin"];

export const FloatingChatLauncher = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const drawerRef = useRef(null);
  const launcherRef = useRef(null);
  const returnFocusRef = useRef(false);
  const hidden = pathname.startsWith("/chat") || PRIVATE_ROUTES.some((route) => pathname.startsWith(route));
  const closeChat = () => { returnFocusRef.current = true; setOpen(false); };

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open && returnFocusRef.current) {
      returnFocusRef.current = false;
      window.requestAnimationFrame(() => launcherRef.current?.focus());
    }
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    const handleDialogKey = (event) => {
      if (event.key === "Escape") { returnFocusRef.current = true; setOpen(false); }
      if (event.key !== "Tab") return;
      const focusable = [...drawerRef.current.querySelectorAll("a, button, textarea, [tabindex]:not([tabindex='-1'])")].filter((element) => !element.disabled);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleDialogKey);
    window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", handleDialogKey); };
  }, [open]);

  if (hidden) return null;
  return (
    <>
      {open && <button type="button" className="floating-chat-backdrop" onClick={closeChat} aria-label="Close SuppleAI overlay" data-testid="floating-chat-backdrop" />}
      {open && (
        <aside ref={drawerRef} id="floating-chat-drawer" className="floating-chat-drawer" role="dialog" aria-modal="true" aria-label="SuppleAI chat" data-testid="floating-chat-drawer">
          <Chatbot embedded onClose={closeChat} closeButtonRef={closeRef} />
        </aside>
      )}
      {!open && (
        <button ref={launcherRef} type="button" className="floating-chat-launcher" onClick={() => setOpen(true)} aria-expanded="false" aria-controls="floating-chat-drawer" data-testid="floating-chat-launcher-btn">
          <MessageCircleMore aria-hidden="true" /><span>Ask SuppleAI</span>
        </button>
      )}
    </>
  );
};